import {ImageUploadChangeValue} from '@/components/SingleImageUpload'
import {useSaveNodeData} from '@/helpers/SaveNodeDataProvider/useNodeDataContext.hook'
import {useJourneyMetaConfig} from '@/hooks/useJourneyMetaConfig'
import {useAppDispatch, useAppSelector} from '@/store'
import {checkNotificationsWarnings} from '@/store/middlewares/nodesDataWarningsListener'
import {NodeTypeForSendNotificationEnum} from '@/types/NodeDataForSendNotification'
import {NodeTypeEnum} from '@/types/NodeTypeEnum'
import {LandingPageItem} from '@/types/StepConfigParams'
import {
  StepSendEmailConfig,
  StepSendNotification,
  StepSendPushNotificationConfig,
  StepSendSMSConfig,
  StepSendWhatsAppConfig,
} from '@/types/Steps'
import {
  resetCurrentNode,
  updateNodeSendEmailData,
  updateNodeSendPushNotificationData,
  updateNodeSendSMSData,
  updateNodeSendWhatsAppData,
  updateNodeWarnings,
} from '@ReduxActions'
import _ from 'lodash'
import {useCallback, useEffect, useState} from 'react'

export const useSendNotificationData = (type: NodeTypeForSendNotificationEnum) => {
  const {id: currentNodeId} = useAppSelector(state => state.currentNode)
  const configRedux = useAppSelector(state => {
    const node = state.nodesData[currentNodeId] as StepSendNotification
    return node?.message_configs?.[0]
  })
  const {setHasUnsavedChanges, setSaveData} = useSaveNodeData()
  const [config, setConfig] = useState(configRedux)
  const {isPublishedOrCompleted} = useJourneyMetaConfig()
  const dispatch = useAppDispatch()

  useEffect(() => {
    setConfig(configRedux)
  }, [configRedux, currentNodeId])

  const updateSendNotificationNode = useCallback(() => {
    switch (type) {
      case NodeTypeEnum.sendPush:
        dispatch(
          updateNodeSendPushNotificationData({
            id: currentNodeId,
            message_configs: [config as StepSendPushNotificationConfig],
          })
        )
        break
      case NodeTypeEnum.sendSMS:
        dispatch(
          updateNodeSendSMSData({
            id: currentNodeId,
            message_configs: [config as StepSendSMSConfig],
          })
        )
        break
      case NodeTypeEnum.sendWhatsApp:
        dispatch(
          updateNodeSendWhatsAppData({
            id: currentNodeId,
            message_configs: [config as StepSendWhatsAppConfig],
          })
        )
        break
      case NodeTypeEnum.sendEmail:
        dispatch(
          updateNodeSendEmailData({
            id: currentNodeId,
            message_configs: [config as StepSendEmailConfig],
          })
        )
        break
    }
  }, [type, dispatch, currentNodeId, config])

  const saveAllTempData = useCallback(
    (hasUnsavedChanges: boolean) => {
      if (isPublishedOrCompleted) return
      if (!hasUnsavedChanges) return

      updateSendNotificationNode()
      dispatch(resetCurrentNode())
    },
    [isPublishedOrCompleted, dispatch, updateSendNotificationNode]
  )

  const checkAndUpdateNotificationsWarnings = useCallback(
    (configParam: typeof configRedux) => {
      const haveWarnings = checkNotificationsWarnings(configParam, type)
      dispatch(updateNodeWarnings({id: currentNodeId, haveWarnings}))
    },
    [currentNodeId, type, dispatch]
  )

  useEffect(() => {
    const hasUnsavedChanges = !_.isEqual(
      {
        config,
      },
      {
        config: configRedux,
      }
    )
    setHasUnsavedChanges(hasUnsavedChanges)

    checkAndUpdateNotificationsWarnings(config)

    setSaveData(() => () => saveAllTempData(hasUnsavedChanges))
  }, [config, configRedux, setHasUnsavedChanges, setSaveData, saveAllTempData, checkAndUpdateNotificationsWarnings])

  const onChangeTemplateAndMessage = useCallback(
    (template: string, message: string) => {
      if (isPublishedOrCompleted) return
      setConfig({...config, id_messagetemplate: template, message})
    },
    [isPublishedOrCompleted, config]
  )

  const onChangeMessage = useCallback((message: string) => {
    setConfig(prev => ({...prev, message}))
  }, [])

  const onChangeRecipient = useCallback(
    (to: string) => {
      if (isPublishedOrCompleted) return
      setConfig({...config, to})
    },
    [config, isPublishedOrCompleted]
  )

  const onChangeSubject = useCallback(
    (subject: string) => {
      if (isPublishedOrCompleted) return
      setConfig({...config, subject})
    },
    [config, isPublishedOrCompleted]
  )

  const onChangeCustomImage = useCallback(
    (value: ImageUploadChangeValue) => {
      if (isPublishedOrCompleted) return

      const valueAsUrl = value ? {id: '', url: value as string, title: null} : undefined
      const image = typeof value === 'string' ? valueAsUrl : value
      setConfig({...config, image})
    },
    [config, isPublishedOrCompleted]
  )

  const onChangeLandingPage = useCallback(
    (landingPage: LandingPageItem) => {
      if (isPublishedOrCompleted) return
      setConfig({...config, landing_page: landingPage})
    },
    [config, isPublishedOrCompleted]
  )

  return {
    config,
    onChangeTemplateAndMessage,
    onChangeMessage,
    onChangeRecipient,
    onChangeSubject,
    onChangeCustomImage,
    onChangeLandingPage,
  }
}
